import { useState } from 'react';
import type { OptionLink, Question } from './types';
import { titleText } from './types';

type Props = {
  /** 当前题（目标题 B）上的选项关联规则；undefined / 空数组 = 未配置 */
  links: OptionLink[] | undefined;
  allQuestions: Question[];
  currentQid: string;
  onChange: (links: OptionLink[] | undefined) => void;
  onClose: () => void;
};

const CHOICE_TYPES = ['single', 'multi', 'select', 'ranking'];

function optionValuesOf(q: Question | undefined): string[] {
  return (q?.options ?? [])
    .map((o) => (typeof o === 'string' ? o : (o as any)?.value))
    .filter((v): v is string => typeof v === 'string' && v !== '');
}

/**
 * 0907「选项关联」弹窗（配置在目标题 B 上，菜单里紧跟「跳题逻辑」）：
 *  - 每条规则：源题 A + A 的某个选项 → 勾选 B 里要联动展示的子选项
 *  - 支持多条（同一源题的不同选项各一条，也可来自不同源题）
 *  - 填写端：A 选中某选项时 B 只展示命中规则的选项（多条命中取并集）；没有任何规则命中时 B 整题不显示
 */
export default function OptionLinkModal({ links, allQuestions, currentQid, onChange, onClose }: Props) {
  const cur = allQuestions.find((q) => q.id === currentQid);
  const ownOptions = optionValuesOf(cur);

  // 显示题号（statement / pagebreak 不占序号）
  const displayNumbers: Record<string, number> = (() => {
    let n = 0;
    const map: Record<string, number> = {};
    for (const q of allQuestions) {
      if (q.type !== 'statement' && q.type !== 'pagebreak') n += 1;
      map[q.id] = n;
    }
    return map;
  })();
  const curNo = displayNumbers[currentQid] ?? 0;
  // 源题：本题之前（按显示题号）的选择题（单选 / 多选 / 下拉 / 排序）。前置题作答先于本题，便于填写者顺序作答
  const sources = allQuestions.filter((q) =>
    q.id !== currentQid
    && CHOICE_TYPES.includes(q.type)
    && optionValuesOf(q).length > 0
    && (displayNumbers[q.id] ?? 0) < curNo,
  );
  const sourceById = new Map(sources.map((q) => [q.id, q]));

  const label = (q: Question) => {
    const t = titleText(q.title);
    return `第 ${displayNumbers[q.id] ?? 0} 题${t ? ` · ${t}` : ''}`;
  };

  const [draft, setDraft] = useState<OptionLink[]>(() =>
    (links ?? [])
      .filter((l) => l && typeof l.questionId === 'string' && typeof l.optionValue === 'string' && Array.isArray(l.options))
      .map((l) => ({ questionId: l.questionId, optionValue: l.optionValue, options: l.options.filter((o) => typeof o === 'string') })),
  );

  const updateAt = (i: number, patch: Partial<OptionLink>) => setDraft((d) => d.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const removeAt = (i: number) => setDraft((d) => d.filter((_, idx) => idx !== i));

  // 新增：沿用上一条的源题；源选项取该源题里还没绑定过的第一个（连点几次就把 A 的每个选项各建一条）
  const addRule = () => {
    const last = draft.length ? sourceById.get(draft[draft.length - 1].questionId) : undefined;
    const src = last ?? sources[0];
    if (!src) return;
    const used = new Set(draft.filter((l) => l.questionId === src.id).map((l) => l.optionValue));
    const opts = optionValuesOf(src);
    const optionValue = opts.find((o) => !used.has(o)) ?? opts[0];
    setDraft((d) => [...d, { questionId: src.id, optionValue, options: [] }]);
  };

  const toggleTarget = (i: number, opt: string, on: boolean) => {
    const l = draft[i];
    const next = on ? [...l.options.filter((o) => o !== opt), opt] : l.options.filter((o) => o !== opt);
    // 按本题选项顺序保存，导出 / 展示顺序稳定
    updateAt(i, { options: ownOptions.filter((o) => next.includes(o)) });
  };

  const handleConfirm = () => {
    const cleaned: OptionLink[] = [];
    for (const l of draft) {
      const src = sourceById.get(l.questionId);
      if (!src || !optionValuesOf(src).includes(l.optionValue)) continue;
      const options = ownOptions.filter((o) => l.options.includes(o));
      if (!options.length) continue; // 没勾任何子选项的规则视为未配置
      cleaned.push({ questionId: l.questionId, optionValue: l.optionValue, options });
    }
    onChange(cleaned.length ? cleaned : undefined);
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: 640 }}>
        <div className="modal-head">
          <h3>选项关联</h3>
          <button className="icon-btn" onClick={onClose} title="关闭">✕</button>
        </div>

        <div className="modal-body">
          <div className="hint" style={{ marginBottom: 4 }}>
            当前是「{cur ? label(cur) : '本题'}」。选择源题目及其选项，再勾选本题要联动展示的子选项：填写者在源题选中该选项时，
            本题只显示勾选的这部分选项（多条规则同时命中时合并显示）；没有任何规则命中时本题整体隐藏，不参与必填。
          </div>

          {ownOptions.length === 0 && <div className="hint hint-bad">本题还没有选项，先添加选项再配置关联。</div>}
          {sources.length === 0 && <div className="hint hint-bad">前面没有可作为源题的选择题（单选 / 多选 / 下拉 / 排序）。选项关联的源题必须是本题之前的题。</div>}

          {draft.length === 0 ? (
            <div className="hint">尚未配置任何绑定，点击下方按钮新增。</div>
          ) : (
            <div className="logic-conds">
              {draft.map((l, i) => {
                const src = sourceById.get(l.questionId);
                const srcOpts = optionValuesOf(src);
                return (
                  <div key={i} className="optlink-rule">
                    <div className="optlink-src">
                      <select
                        className="input"
                        value={sourceById.has(l.questionId) ? l.questionId : ''}
                        onChange={(e) => {
                          const nextSrc = sourceById.get(e.target.value);
                          updateAt(i, { questionId: e.target.value, optionValue: optionValuesOf(nextSrc)[0] ?? '' });
                        }}
                        aria-label="源题目"
                      >
                        <option value="">选择源题目</option>
                        {sources.map((q) => <option key={q.id} value={q.id}>{label(q)}</option>)}
                      </select>
                      <span className="jump-arrow">选</span>
                      <select
                        className="input"
                        value={srcOpts.includes(l.optionValue) ? l.optionValue : ''}
                        onChange={(e) => updateAt(i, { optionValue: e.target.value })}
                        disabled={!src}
                        aria-label="源题目的选项"
                      >
                        <option value="">选择源选项</option>
                        {srcOpts.map((v) => <option key={v} value={v}>{v}</option>)}
                      </select>
                      <button className="icon-btn icon-danger" onClick={() => removeAt(i)} title="删除这条绑定">✕</button>
                    </div>
                    <div className="optlink-targets">
                      <span className="optlink-targets-label">
                        源题选「{l.optionValue || '…'}」时，本题只显示：
                        <button type="button" className="optlink-mini" onClick={() => updateAt(i, { options: [...ownOptions] })}>全选</button>
                        <button type="button" className="optlink-mini" onClick={() => updateAt(i, { options: [] })}>清空</button>
                      </span>
                      {ownOptions.map((o) => {
                        const on = l.options.includes(o);
                        return (
                          <label key={o} className={`optlink-chip${on ? ' is-on' : ''}`}>
                            <input type="checkbox" checked={on} onChange={(e) => toggleTarget(i, o, e.target.checked)} />
                            {o}
                          </label>
                        );
                      })}
                    </div>
                    {l.options.length === 0 && <div className="hint hint-bad">还没勾选任何子选项，点「确定」时这条绑定会被忽略。</div>}
                  </div>
                );
              })}
            </div>
          )}

          <button type="button" className="optlink-add" onClick={addRule} disabled={sources.length === 0 || ownOptions.length === 0}>
            + 添加绑定{draft.length > 0 ? '（自动带出源题的下一个选项）' : ''}
          </button>
        </div>

        <div className="modal-foot">
          <button className="btn" onClick={onClose}>取消</button>
          <button className="btn btn-primary" onClick={handleConfirm}>确定</button>
        </div>
      </div>
    </div>
  );
}
